/**
 * User-coupon (direct assignment) mutations using React Relay.
 *
 * Used by the coupon admin UI to grant a coupon directly to a specific user
 * (`assignUserCoupon`) and to revoke an existing grant (`revokeUserCoupon`).
 *
 * Note: Run `npm run relay` after editing GraphQL queries to regenerate
 * type artifacts under __generated__.
 */

import { useCallback } from 'react';
import { useMutation, commitMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { MutationResult, CouponMutationError } from './useCouponMutations';

// ============================================================================
// GraphQL Mutations
// ============================================================================

const AssignUserCouponMutation = graphql`
  mutation useUserCouponMutationsAssignMutation($input: AssignUserCouponInput!) {
    assignUserCoupon(input: $input) {
      result {
        id
        assignedBy
        couponOldId
        userOldId
        userId
        created
        timesUsed
        removedOn
      }
      errors {
        message
        fields
        code
        shortMessage
        vars
      }
    }
  }
`;

const RevokeUserCouponMutation = graphql`
  mutation useUserCouponMutationsRevokeMutation($id: ID!) {
    revokeUserCoupon(id: $id) {
      result {
        id
        removedOn
      }
      errors {
        message
        fields
        code
        shortMessage
        vars
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface AssignUserCouponInput {
  /** The coupon's legacy `oldId`. */
  couponOldId: string;
  /** The user's phone / legacy id. */
  userOldId: string;
  /** The user's UUID. */
  userId?: string | null;
  /** The acting admin's UUID (audit trail). */
  assignedBy?: string | null;
}

// ============================================================================
// Hooks
// ============================================================================

export function useAssignUserCoupon() {
  const [commit, isInFlight] = useMutation<any>(AssignUserCouponMutation);

  const assignUserCoupon = useCallback(
    (input: AssignUserCouponInput) => {
      return new Promise<MutationResult<any>>((resolve) => {
        commit({
          variables: { input },
          onCompleted: (response: any) => {
            const result = response?.assignUserCoupon;
            const errors = (result?.errors ?? []) as CouponMutationError[];
            resolve({
              data: result?.result ?? null,
              errors,
              isSuccess: errors.length === 0 && result?.result != null,
            });
          },
          onError: (error: Error) => {
            resolve({
              data: null,
              errors: [{ message: error.message }],
              isSuccess: false,
            });
          },
        });
      });
    },
    [commit]
  );

  return { assignUserCoupon, isLoading: isInFlight };
}

export function useRevokeUserCoupon() {
  const [commit, isInFlight] = useMutation<any>(RevokeUserCouponMutation);

  const revokeUserCoupon = useCallback(
    (id: string) => {
      return new Promise<MutationResult<any>>((resolve) => {
        commit({
          variables: { id },
          onCompleted: (response: any) => {
            const result = response?.revokeUserCoupon;
            const errors = (result?.errors ?? []) as CouponMutationError[];
            resolve({
              data: result?.result ?? null,
              errors,
              isSuccess: errors.length === 0 && result?.result != null,
            });
          },
          onError: (error: Error) => {
            resolve({
              data: null,
              errors: [{ message: error.message }],
              isSuccess: false,
            });
          },
        });
      });
    },
    [commit]
  );

  return { revokeUserCoupon, isLoading: isInFlight };
}

// ============================================================================
// Imperative API (without hooks)
// ============================================================================

export function assignUserCouponMutation(
  input: AssignUserCouponInput
): Promise<MutationResult<unknown>> {
  return new Promise((resolve) => {
    commitMutation<any>(relayEnvironment, {
      mutation: AssignUserCouponMutation,
      variables: { input },
      onCompleted: (response: any) => {
        const result = response?.assignUserCoupon;
        const errors = (result?.errors ?? []) as CouponMutationError[];
        resolve({
          data: result?.result ?? null,
          errors,
          isSuccess: errors.length === 0 && result?.result != null,
        });
      },
      onError: (error: Error) => {
        resolve({
          data: null,
          errors: [{ message: error.message }],
          isSuccess: false,
        });
      },
    });
  });
}

export function revokeUserCouponMutation(id: string): Promise<MutationResult<unknown>> {
  return new Promise((resolve) => {
    commitMutation<any>(relayEnvironment, {
      mutation: RevokeUserCouponMutation,
      variables: { id },
      onCompleted: (response: any) => {
        const result = response?.revokeUserCoupon;
        const errors = (result?.errors ?? []) as CouponMutationError[];
        resolve({
          data: result?.result ?? null,
          errors,
          isSuccess: errors.length === 0 && result?.result != null,
        });
      },
      onError: (error: Error) => {
        resolve({
          data: null,
          errors: [{ message: error.message }],
          isSuccess: false,
        });
      },
    });
  });
}
